<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useFunctionGetter, useMapGetter } from 'dashboard/composables/store';
import { useAdmin } from 'dashboard/composables/useAdmin';
import NextButton from 'dashboard/components-next/button/Button.vue';
import EditTicketModal from 'dashboard/components/tickets/EditTicketModal.vue';
import EscalateToJiraModal from 'dashboard/components/tickets/EscalateToJiraModal.vue';
import EscalateToPlaneModal from 'dashboard/components/tickets/EscalateToPlaneModal.vue';
import ViewTicketMessagesModal from 'dashboard/components/tickets/ViewTicketMessagesModal.vue';
import DeleteTicketConfirmationModal from 'dashboard/components/tickets/DeleteTicketConfirmationModal.vue';
import { formatDate } from 'shared/helpers/DateHelper';
import { emitter } from 'shared/helpers/mitt';

const props = defineProps({
  ticket: {
    type: Object,
    required: true,
  },
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const emit = defineEmits(['refresh']);

const { t } = useI18n();
const store = useStore();
const { isAdmin } = useAdmin();
const isUpdating = ref(false);
const isDeleting = ref(false);
const showEditModal = ref(false);
const showEscalateJiraModal = ref(false);
const showEscalatePlaneModal = ref(false);
const showViewMessagesModal = ref(false);
const showDeleteConfirmation = ref(false);

// Integration checks
const jiraIntegration = useFunctionGetter('integrations/getIntegration', 'jira');
const isJiraEnabled = computed(() => {
  return !!jiraIntegration.value?.enabled;
});

const planeIntegration = useFunctionGetter('integrations/getIntegration', 'plane');
const isPlaneEnabled = computed(() => {
  return !!planeIntegration.value?.enabled;
});

// Check if current user is administrator and not a custom role
const currentUser = computed(() => store.getters.getCurrentUser);
const currentCustomRoleId = computed(() => store.getters.getCurrentCustomRoleId);
const isAdministrator = computed(() => isAdmin.value && !currentCustomRoleId.value);

const ticketTitle = computed(() => props.ticket.title || 'Untitled Ticket');
const ticketDescription = computed(() => props.ticket.description || '');
const ticketStatus = computed(() => props.ticket.status || 'open');
const ticketPriority = computed(() => props.ticket.priority || 'medium');
const isResolved = computed(() => ticketStatus.value === 'resolved' || ticketStatus.value === 'closed');
const isEscalatedToJira = computed(() => !!props.ticket.jira_issue_key);
const isEscalatedToPlane = computed(() => !!props.ticket.plane_issue_id);
const isEscalated = computed(() => ticketStatus.value === 'escalated' || isEscalatedToJira.value || isEscalatedToPlane.value);

const createdAt = computed(() => {
  if (props.ticket.created_at) {
    return formatDate(new Date(props.ticket.created_at), 'MMM dd, yyyy');
  }
  return '';
});

const getStatusColor = (status) => {
  const colors = {
    open: 'bg-blue-50 text-blue-700 border-blue-200',
    in_progress: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    escalated: 'bg-orange-50 text-orange-700 border-orange-200',
    resolved: 'bg-green-50 text-green-700 border-green-200',
    closed: 'bg-gray-50 text-gray-700 border-gray-200',
  };
  return colors[status] || colors.open;
};

const getPriorityColor = (priority) => {
  const colors = {
    low: 'bg-gray-50 text-gray-700 border-gray-200',
    medium: 'bg-blue-50 text-blue-700 border-blue-200',
    high: 'bg-orange-50 text-orange-700 border-orange-200',
    urgent: 'bg-red-50 text-red-700 border-red-200',
  };
  return colors[priority] || colors.medium;
};

const markAsDone = async () => {
  if (isResolved.value) return;
  
  isUpdating.value = true;
  try {
    await store.dispatch('tickets/updateTicket', {
      id: props.ticket.id,
      status: 'resolved'
    });
    useAlert(t('TICKETS.MARK_AS_DONE_SUCCESS'));
    emit('refresh');
  } catch (error) {
    console.error('Failed to mark ticket as done:', error);
    useAlert(t('TICKETS.MARK_AS_DONE_ERROR'));
  } finally {
    isUpdating.value = false;
  }
};

const openEditModal = () => {
  showEditModal.value = true;
};

const closeEditModal = () => {
  showEditModal.value = false;
};

const onTicketUpdated = () => {
  emit('refresh');
  closeEditModal();
};

const openEscalateJiraModal = () => {
  showEscalateJiraModal.value = true;
};

const closeEscalateJiraModal = () => {
  showEscalateJiraModal.value = false;
};

const onTicketEscalatedToJira = () => {
  emit('refresh');
  closeEscalateJiraModal();
};

const openEscalatePlaneModal = () => {
  showEscalatePlaneModal.value = true;
};

const closeEscalatePlaneModal = () => {
  showEscalatePlaneModal.value = false;
};

const onTicketEscalatedToPlane = () => {
  emit('refresh');
  closeEscalatePlaneModal();
};

const openViewMessagesModal = () => {
  showViewMessagesModal.value = true;
};

const closeViewMessagesModal = () => {
  showViewMessagesModal.value = false;
};

const openDeleteConfirmation = () => {
  showDeleteConfirmation.value = true;
};

const closeDeleteConfirmation = () => {
  showDeleteConfirmation.value = false;
};

const confirmDeleteTicket = async () => {
  isDeleting.value = true;
  try {
    await store.dispatch('tickets/deleteTicket', props.ticket.id);
    useAlert(t('TICKETS.DELETE_SUCCESS'));
    emit('refresh');
    closeDeleteConfirmation();
  } catch (error) {
    console.error('Failed to delete ticket:', error);
    if (error.response?.status === 403) {
      useAlert(t('TICKETS.DELETE_ACCESS_DENIED'));
    } else {
      useAlert(t('TICKETS.DELETE_ERROR'));
    }
  } finally {
    isDeleting.value = false;
  }
};

const viewJiraIssue = () => {
  if (props.ticket.jira_url) {
    window.open(props.ticket.jira_url, '_blank');
  }
};

// WebSocket event listeners for real-time updates
const handleTicketUpdate = (data) => {
  if (data.ticket_id === props.ticket.id) {
    console.log('TicketItem: Received real-time update for this ticket', data);
    // Emit refresh to parent to reload ticket data
    emit('refresh');
    
    // Show notification if ticket was auto-resolved by JIRA
    if (data.status === 'resolved' && data.jira_issue_key) {
      useAlert(t('TICKETS.JIRA_AUTO_RESOLVED_NOTIFICATION'));
    }
  }
};

const handleJiraIssueUpdate = (data) => {
  if (data.conversation_id === props.conversationId && props.ticket.jira_issue_key) {
    console.log('TicketItem: Received JIRA status update for conversation', data);
    // If this ticket is linked to the updated JIRA issue, refresh
    if (data.issue_key === props.ticket.jira_issue_key && data.completed) {
      emit('refresh');
    }
  }
};

const handleTicketDeleted = (data) => {
  if (data.ticket_id === props.ticket.id) {
    console.log('TicketItem: This ticket was deleted remotely', data);
    // Emit refresh to parent to remove this ticket from the list
    emit('refresh');
  }
};

onMounted(() => {
  // Ensure integrations are loaded for conditional escalation buttons
  store.dispatch('integrations/get', 'jira');
  store.dispatch('integrations/get', 'plane');
  // Listen for ticket updates
  emitter.on('tickets:ticket-updated', handleTicketUpdate);
  emitter.on('jira:ticket-auto-resolved', handleTicketUpdate);
  emitter.on('jira:issue-status-updated', handleJiraIssueUpdate);
  emitter.on('tickets:ticket-deleted', handleTicketDeleted);
});

onUnmounted(() => {
  // Clean up event listeners
  emitter.off('tickets:ticket-updated', handleTicketUpdate);
  emitter.off('jira:ticket-auto-resolved', handleTicketUpdate);
  emitter.off('jira:issue-status-updated', handleJiraIssueUpdate);
  emitter.off('tickets:ticket-deleted', handleTicketDeleted);
});
</script>

<template>
  <div class="p-4 rounded-xl hover:bg-n-alpha-1 transition-all duration-200 shadow-sm hover:shadow-md border border-n-weak">
    <div class="flex items-start justify-between gap-4">
      <div class="flex-1 min-w-0">
        <!-- Ticket header -->
        <div class="flex items-center gap-3 mb-3">
          <button
            class="inline-flex items-center gap-2 text-purple-700 hover:text-purple-800 font-semibold text-sm bg-purple-50 hover:bg-purple-100 px-3 py-1.5 rounded-lg transition-colors"
            @click="openEditModal"
          >
            <div class="w-4 h-4 bg-purple-600 rounded text-white text-xs flex items-center justify-center font-bold">
              T
            </div>
            #{{ ticket.id }}
          </button>
          
          <span v-if="createdAt" class="text-xs text-n-slate-10 hidden sm:block">
            {{ $t('TICKETS.CREATED') }}: {{ createdAt }}
          </span>
        </div>

        <!-- Ticket title -->
        <h4 class="text-sm font-medium text-n-slate-12 mb-3 line-clamp-2 leading-relaxed">
          {{ ticketTitle }}
        </h4>

        <!-- Ticket description -->
        <p v-if="ticketDescription" class="text-xs text-n-slate-10 mb-3 line-clamp-2">
          {{ ticketDescription }}
        </p>

        <!-- Ticket metadata -->
        <div class="flex flex-wrap items-center gap-2 mb-4">
          <span
            class="px-3 py-1.5 rounded-full text-xs font-medium border shadow-sm"
            :class="getStatusColor(ticketStatus)"
          >
            {{ $t(`TICKETS.STATUS.${ticketStatus.toUpperCase()}`) }}
          </span>
          <span
            class="px-3 py-1.5 rounded-full text-xs font-medium border shadow-sm"
            :class="getPriorityColor(ticketPriority)"
          >
            {{ $t(`TICKETS.PRIORITY.${ticketPriority.toUpperCase()}`) }}
          </span>
          
          <!-- Category Badge -->
          <span
            v-if="ticket.category"
            class="px-3 py-1.5 rounded-full text-xs font-medium border shadow-sm bg-purple-50 text-purple-700 border-purple-200"
          >
            {{ ticket.category }}
          </span>
          
          <!-- JIRA Issue Badge -->
          <button
            v-if="ticket.jira_issue_key"
            class="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 rounded-full hover:bg-blue-100 transition-colors"
            @click="viewJiraIssue"
            :title="$t('TICKETS.VIEW_IN_JIRA')"
          >
            <i class="ri-external-link-line text-xs"></i>
            {{ ticket.jira_issue_key }}
          </button>
          
          <!-- Plane Issue Badge -->
          <span
            v-if="ticket.plane_issue_id"
            class="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-full"
          >
            <i class="ri-plane-line text-xs"></i>
            {{ $t('TICKETS.PLANE_LINKED') }}
          </span>
        </div>

        <!-- Action buttons -->
        <div class="flex items-center gap-2 flex-wrap">
          <NextButton
            v-if="!isResolved"
            size="tiny"
            variant="ghost"
            color-scheme="secondary"
            :is-loading="isUpdating"
            class="hover:bg-green-50 hover:text-green-700"
            @click="markAsDone"
          >
            <i class="ri-check-line" />
            {{ $t('TICKETS.MARK_AS_DONE') }}
          </NextButton>
          
          <NextButton
            v-if="isJiraEnabled && !isEscalatedToJira && !isResolved"
            size="tiny"
            variant="ghost"
            color-scheme="secondary"
            class="hover:bg-orange-50 hover:text-orange-700"
            @click="openEscalateJiraModal"
          >
            <i class="ri-external-link-line" />
            {{ $t('TICKETS.ESCALATE_TO_JIRA') }}
          </NextButton>
          
          <NextButton
            v-if="isPlaneEnabled && !isEscalatedToPlane && !isResolved"
            size="tiny"
            variant="ghost"
            color-scheme="secondary"
            class="hover:bg-indigo-50 hover:text-indigo-700"
            @click="openEscalatePlaneModal"
          >
            <i class="ri-plane-line" />
            {{ $t('TICKETS.ESCALATE_TO_PLANE') }}
          </NextButton>
          
          <NextButton
            v-if="!isResolved"
            size="tiny"
            variant="ghost"
            color-scheme="secondary"
            class="hover:bg-blue-50 hover:text-blue-700"
            @click="openEditModal"
          >
            <i class="ri-edit-line" />
            {{ $t('TICKETS.EDIT_TICKET') }}
          </NextButton>
          
          <NextButton
            size="tiny"
            variant="ghost"
            color-scheme="secondary"
            class="hover:bg-slate-50 hover:text-slate-700"
            @click="openViewMessagesModal"
          >
            <i class="ri-message-3-line" />
            {{ $t('TICKETS.VIEW_MESSAGES') }}
          </NextButton>

          <!-- Delete button (Admin only) -->
          <NextButton
            v-if="isAdministrator"
            size="tiny"
            variant="ghost"
            color-scheme="danger"
            class="hover:bg-red-50 hover:text-red-700"
            :loading="isDeleting"
            @click="openDeleteConfirmation"
          >
            <i class="ri-delete-bin-line" />
            {{ $t('TICKETS.DELETE_TICKET') }}
          </NextButton>
        </div>
      </div>
    </div>
    
    <!-- Modals -->
    <EditTicketModal
      v-if="showEditModal"
      :ticket="ticket"
      @close="closeEditModal"
      @updated="onTicketUpdated"
    />
    
    <EscalateToJiraModal
      v-if="showEscalateJiraModal"
      :ticket="ticket"
      @close="closeEscalateJiraModal"
      @escalated="onTicketEscalatedToJira"
    />
    
    <EscalateToPlaneModal
      v-if="showEscalatePlaneModal"
      :ticket="ticket"
      @close="closeEscalatePlaneModal"
      @escalated="onTicketEscalatedToPlane"
    />
    
        <ViewTicketMessagesModal
      v-if="showViewMessagesModal"
      :ticket="ticket"
      @close="closeViewMessagesModal"
    />

    <!-- Delete Confirmation Modal -->
    <DeleteTicketConfirmationModal
      :show="showDeleteConfirmation"
      :is-deleting="isDeleting"
      :ticket="ticket"
      @confirm="confirmDeleteTicket"
      @cancel="closeDeleteConfirmation"
    />
  </div>
</template>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
